import { isMqDesktop, isMqMobile } from 'mk/common/responsive';
import { deviceChanged, orientationChanged, softKeyboardToggle, viewportChanged } from 'mk2/actions';
import {
    getRequestDeviceDesktop,
    getRequestDeviceIsIOS,
    getRequestDeviceMobile,
    getRequestDeviceViewport,
} from 'mk2/reducers';
import { eventChannel } from 'redux-saga';
import { all, call, fork, put, select, take } from 'redux-saga/effects';

function watchKeyboard() {
    return eventChannel((emitter) => {

        // Debounce focusout event
        const DELAY = 300;
        let timer;

        const listener = (event) => {
            clearTimeout(timer);
            if (event.type === 'focusout') {
                timer = setTimeout(() => emitter({ event }), DELAY);
            } else {
                emitter({ event });
            }
        };

        window.addEventListener('orientationchange', listener);
        window.addEventListener('resize', listener);
        window.addEventListener('focusin', listener);
        window.addEventListener('focusout', listener);

        return () => {
            window.removeEventListener('resize', listener);
            window.removeEventListener('focusin', listener);
            window.removeEventListener('focusout', listener);
            window.removeEventListener('orientationchange', listener);
        };
    });
}

function* deviceChanges() {
    // Register events
    if (typeof window === 'undefined') {
        // ignore
        return;
    }

    const ios: boolean = yield select(getRequestDeviceIsIOS);

    const channel = yield call(watchKeyboard);

    while (true) {
        const { event } = yield take(channel);

        const mobile = isMqMobile();
        const desktop = isMqDesktop();

        const oldMobile = yield select(getRequestDeviceMobile);
        const oldDesktop = yield select(getRequestDeviceDesktop);

        if (oldMobile !== mobile || oldDesktop !== desktop) {
            yield put(deviceChanged(mobile, desktop));
            // Set cookie, so that we can use it during server side rendering
            document.cookie = `ssr_device=${mobile ? '1' : '0'}${desktop ? '1' : '0'}; path=/`;
        }

        let isFocused = false;
        if (document.activeElement) { // IE 11 - active item is sometime null
            const activeTagName = document.activeElement.tagName.toUpperCase();
            isFocused = activeTagName === 'INPUT' || activeTagName === 'TEXTAREA';
        }

        if (ios) {

            // TODO: Do some research and write constants based on iOS versions
            const keyboardHeight = 200;

            if (isFocused && event.type === 'focusin') {
                yield put(softKeyboardToggle(true, keyboardHeight));
            } else if (event.type === 'focusout') {
                yield put(softKeyboardToggle(false, keyboardHeight));
            }

        } else {

            // TODO: What about tablets?
            if (!mobile) {
                continue;
            }

            const viewport = yield select(getRequestDeviceViewport);

            const width = document.documentElement.clientWidth || window.innerWidth;
            const height = document.documentElement.clientHeight || window.innerHeight;

            if (width !== viewport.width || height !== viewport.height) {
                yield put(viewportChanged(width, height));
            }

            if (viewport.width !== width && viewport.height !== height) {
                // Rotated - blur current element
                if (isFocused) {
                    document.documentElement.blur();
                }
                yield put(orientationChanged(width > height ? 'landscape' : 'portrait'));
            } else if (Math.abs(height - viewport.height) < 150) {
                // too small for keyboard - ignore
            } else if (height < viewport.height && isFocused) {
                // keyboard opened
                yield put(softKeyboardToggle(true, viewport.height - height));
            } else if (height > viewport.height) {
                // keyboard closed
                yield put(softKeyboardToggle(false, height - viewport.height));
            }

        }
    }
}

export default function* root() {
    yield all([
        fork(deviceChanges),
    ]);
}
